<template>
  <div class="px-2.5">
    <div class="flex items-center gap-5 font-medium cursor-pointer" @click="onAddMenu()">
      <BaseButton
        class="rounded w-9 h-9 text-theme-500 bg-cyan-light border-cyan-light hover:text-white"
        size="icon"
        color="custom">
        <Icon icon="teenyicons:add-outline" width="16" />
      </BaseButton>
      Add a Menu
    </div>
    <div class="flex flex-col gap-1.5 mt-5">
      <div
        v-for="(menu, index) in filteredMenus"
        :key="menu.key"
        class="flex flex-col gap-1 text-sm font-medium rounded-md">
        <input
          v-if="showRenameInput && contextMenuTargetIndex == index"
          type="text"
          ref="renameInput"
          v-model="newMenuName"
          class="p-2 border border-theme-500 bg-theme-200 focus:outline-theme-500"
          @keyup.enter="onMenuRename"
          @blur="onMenuRename" />
        <div
          v-else
          tabindex="0"
          class="flex items-center gap-4 p-2.5 rounded-md cursor-pointer"
          :class="[menu.pinnedTop ? [menu.color] : 'text-theme-600 bg-theme-50']"
          @click="onMenuClick(menu.key)"
          @contextmenu.prevent="showContextMenu($event, menu.key)">
          <Icon v-if="menu.pinnedTop" icon="carbon:pin-filled" width="24" />
          {{ menu.name }}
          <Icon
            v-if="menu.items.length"
            icon="carbon:chevron-down"
            class="ml-auto transition duration-200 ease-in-out text-theme-600"
            width="16"
            :class="{ 'rotate-180': menu.isExpanded }" />
        </div>

        <!-- menu items -->
        <div v-if="menu.isExpanded">
          <div
            v-for="(item, index) in menu.items"
            :key="index"
            class="p-2.5 cursor-pointer hover:bg-slate-100 hover:text-theme-600 flex items-center justify-between text-slate-600 font-light rounded-md"
            @click="navigateToMenuItemPath(item)">
            {{ item.name }}
            <Icon
              icon="fluent:heart-24-filled"
              class="w-4 h-4 cursor-pointer text-slate-400 hover:text-theme-500"
              @click="onRemoveMenuItem(menu.key, index)" />
          </div>
        </div>
      </div>
    </div>
    <ContextMenu
      ref="menu"
      :options="contextMenuOptions"
      class="fixed"
      :style="{ top: clickLocation.y + 'px', left: clickLocation.x + 'px' }"
      :isDropdownOpen="isContextMenuOpen"
      @action="onAction($event)"
      @close="isContextMenuOpen = false" />
  </div>
  <InfoModal
    v-if="showDeleteModal"
    :text="`Are you sure you want to delete menu ${menus[contextMenuTargetIndex].name}?`"
    secondaryButtonText="Delete"
    primaryButtonText="Cancel"
    @primaryButtonClicked="showDeleteModal = false"
    @secondaryButtonClicked="onMenuDelete" />
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted } from 'vue';
import { Icon } from '@iconify/vue';
import BaseButton from '../BaseButton.vue';
import ContextMenu from './ContextMenu.vue';
import { getUserPreferences, updateUserPreferences } from '@/services/api/user';
import InfoModal from '../InfoModal.vue';
import type { FavoriteMenu, FavoriteMenuItem } from '@/types/ComponentTypes';
import { v4 as uuidv4 } from 'uuid';
import { currentDateToISO } from '@/utils/date';
import { useRouter } from 'vue-router';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { useToastStore } from '@/stores/toast';

const toastStore = useToastStore();

const menus = ref<FavoriteMenu[]>([]);

const props = defineProps<{
  searchTerm?: string;
  filterFunction: Function;
}>();

const emit = defineEmits(['updateFavorites']);

const router = useRouter();

onMounted(async () => {
  const resp = await getUserPreferences();
  if (!resp) return;
  menus.value = resp
    .filter((x) => x.preferenceType === 'Favorites save' && x.preferenceContent != null)
    .map((x) => JSON.parse(x.preferenceContent as string));
  emit('updateFavorites', menus.value);
});

async function onAddMenu(name?: string, duplicateKey: number = -1, items: FavoriteMenuItem[] = []) {
  const newMenuItem = {
    name: name || `New Menu${menus.value.length > 0 ? menus.value.length : ''}`,
    items: duplicateKey >= 0 ? menus.value[duplicateKey].items : items,
    color: getRandomColor(),
    key: uuidv4(),
    isExpanded: false,
    pinnedTop: false,
    pinnedMain: duplicateKey >= 0 ? menus.value[duplicateKey].pinnedMain : false,
    pathName: '',
  };
  if (newMenuItem.items.length > 0) newMenuItem.isExpanded = true;
  menus.value.push(newMenuItem);

  showRenameInput.value = true;
  contextMenuTargetIndex.value = menus.value.length - 1;
  contextMenuTargetKey.value = newMenuItem.key;

  nextTick(() => {
    if (renameInput.value) {
      renameInput.value[0].focus();
      renameInput.value[0].select();
    }
  });
  newMenuName.value = newMenuItem.name;

  await updateServer('Added');
}

const colors = ref([
  'text-red-500',
  'text-blue-500',
  'text-green-500',
  'text-yellow-500',
  'text-purple-500',
  'text-pink-500',
  'text-indigo-500',
  'text-fuchsia-500',
  'text-emerald-500',
  'text-teal-500',
  'text-orange-500',
  'text-lime-500',
  'text-violet-500',
  'text-sky-500',
  'text-rose-500',
]);

function getRandomColor() {
  return colors.value[Math.floor(Math.random() * colors.value.length)];
}

const isContextMenuOpen = ref(false);

const contextMenuTargetIndex = ref(-1);
const contextMenuTargetKey = ref();
const clickLocation = ref({ x: 0, y: 0 });

const showContextMenu = (event: MouseEvent, key: string) => {
  clickLocation.value = {
    x: event.clientX,
    y: event.clientY,
  };
  contextMenuTargetIndex.value = findMenuIndexByKey(key);
  contextMenuTargetKey.value = key;
  isContextMenuOpen.value = true;
};

const contextMenuOptions = computed(() => {
  if (contextMenuTargetIndex.value < 0 || !menus.value) return [];
  const actions = [
    { name: 'tree.rename', action: 'rename', icon: 'fluent:rename-24-regular', color: '' },
    { name: 'tree.duplicate', action: 'duplicate', icon: 'fad:duplicate', color: '' },
  ];
  if (menus.value[contextMenuTargetIndex.value].pinnedTop)
    actions.push({
      name: 'Unpin from top',
      action: 'unpin-top',
      icon: 'carbon:pin',
      color: 'text-red-500',
    });
  else actions.push({ name: 'Pin to top', action: 'pin-top', icon: 'carbon:pin', color: '' });
  if (menus.value[contextMenuTargetIndex.value].pinnedMain)
    actions.push({
      name: 'Unpin from main menu',
      action: 'unpin-menu',
      icon: 'carbon:pin',
      color: 'text-red-500',
    });
  else
    actions.push({
      name: 'Pin to Main Menu',
      action: 'pin-menu',
      icon: 'carbon:pin',
      color: '',
    });
  actions.push({
    name: 'tree.deleteItem',
    action: 'delete',
    icon: 'tdesign:delete-1',
    color: 'text-red-500',
  });
  return actions;
});

async function onAction(action: string) {
  if (action === 'unpin-top' || action === 'pin-top')
    menus.value[contextMenuTargetIndex.value].pinnedTop =
      !menus.value[contextMenuTargetIndex.value].pinnedTop;
  else if (action === 'duplicate') {
    onAddMenu(menus.value[contextMenuTargetIndex.value].name + '(1)', contextMenuTargetIndex.value);
  } else if (action === 'delete') showDeleteModal.value = true;
  else if (action === 'rename') {
    showRenameInput.value = true;
    nextTick(() => {
      if (renameInput.value) {
        renameInput.value[0].focus();
        renameInput.value[0].select();
      }
    });
    newMenuName.value = menus.value[contextMenuTargetIndex.value].name;
  } else if (action == 'pin-menu' || action == 'unpin-menu') {
    menus.value[contextMenuTargetIndex.value].pinnedMain =
      !menus.value[contextMenuTargetIndex.value].pinnedMain;
    menus.value[contextMenuTargetIndex.value].isExpanded = false;
  }
  emit('updateFavorites', menus.value);
  isContextMenuOpen.value = false;
  if (action != 'duplicate' && action != 'delete' && action != 'rename')
    await updateServer('Modified');
}

const sortedMenus = computed(() => {
  return menus.value
    ? [...menus.value].sort((a, b) => Number(b.pinnedTop) - Number(a.pinnedTop))
    : [];
});

function findMenuIndexByKey(menuKey: string) {
  return menus.value.findIndex((item: FavoriteMenu) => item.key == menuKey);
}
function onMenuClick(menuKey: string) {
  const index = findMenuIndexByKey(menuKey);
  if (index > -1) {
    if (!menus.value[index].items.length) return;
    menus.value[index].isExpanded = !menus.value[index].isExpanded;
  }
}

const showDeleteModal = ref(false);
async function onMenuDelete() {
  await updateServer('Deleted');
  menus.value.splice(contextMenuTargetIndex.value, 1);
  showDeleteModal.value = false;
  contextMenuTargetIndex.value = -1;
  contextMenuTargetKey.value = '';
  emit('updateFavorites', menus.value);
}

const showRenameInput = ref(false);
const renameInput = ref<HTMLInputElement[] | null>(null);
const newMenuName = ref<string>('');

async function onMenuRename() {
  validationModel.value.newName = newMenuName.value || '';
  $v.value.$touch();
  if ($v.value.$invalid) {
    toastStore.error({ text: 'Name is required.' });
    showRenameInput.value = false;
    contextMenuTargetIndex.value = -1;
    contextMenuTargetKey.value = '';
    return;
  }

  showRenameInput.value = false;
  let index = contextMenuTargetIndex.value;
  menus.value[index].name = newMenuName.value;
  await updateServer('Modified');
}

async function onRemoveMenuItem(key: string, itemIndex: number) {
  const index = findMenuIndexByKey(key);
  if (index > -1) {
    menus.value[index].items.splice(itemIndex, 1);
    await updateServer('Modified', index);
  }
}

async function onAddMenuItem(key: string, item: FavoriteMenuItem) {
  const index = findMenuIndexByKey(key);
  if (index > -1 && !menus.value[index].items.some((e) => e.name === item.name)) {
    menus.value[index].items.push(item);
    await updateServer('Modified', index);
  }
}

const filteredMenus = computed(() => {
  if (!props.searchTerm) return sortedMenus.value;
  return (
    props.filterFunction(menus.value, props.searchTerm.toLocaleLowerCase()) ?? sortedMenus.value
  );
});

async function updateServer(
  action: 'Deleted' | 'Added' | 'Modified',
  index: number = contextMenuTargetIndex.value
) {
  await updateUserPreferences({
    id: menus.value[index].key,
    designation: menus.value[index].name,
    preferenceContent: JSON.stringify(menus.value[index]),
    userID: JSON.parse(localStorage.getItem('sessionDetails')!).userID,
    isDefault: false,
    preferenceType: 'Favorites save',
    rowStatus: action,
    rowVersion: currentDateToISO(),
    isActive: true,
    isFromManager: true,

    createUserID: JSON.parse(localStorage.getItem('sessionDetails')!).userID,
  });
}

function navigateToMenuItemPath(item: FavoriteMenuItem) {
  if (item.pathName) router.push(item.pathName);
}

const validationModel = ref({ newName: '' });
const rules = computed(() => ({
  newName: { required: helpers.withMessage('Name is required', required) },
}));
const $v = useVuelidate(rules, validationModel, { $lazy: true });

defineExpose({
  contextMenuTargetIndex,
  contextMenuTargetKey,
  onAction,
  onMenuClick,
  onRemoveMenuItem,
  onAddMenuItem,
  findMenuIndexByKey,
  showRenameInput,
  onMenuRename,
  onAddMenu,
});
</script>
